<template lang="pug">
.monaco-editor-container(
    ref="container"
    :style="{ '--height': height + 'px', '--min-height': minHeight + 'px' }"
    :class="{ disabled, focused }"
)
    .editor
        vue-monaco-editor(
            ref="editor"
            v-model:value="content"
            theme="vs-dark"
            :language="language === 'js' ? 'javascript' : language"
            :options="options"
            @mount="handleMount"
        )
</template>

<script>
import '@fontsource/fira-code'
// import '@fontsource/jetbrains-mono'
// import '@fontsource/monaspace-krypton';

import { VueMonacoEditor } from '@guolao/vue-monaco-editor'
import oneDarkTheme from '@/assets/monaco-themes/one-dark.json'
import githubDarkTheme from '@/assets/monaco-themes/github-dark.json'

// Module-level: shared twig completions across all editor instances.
// The Monaco provider is registered exactly once and reads these arrays
// fresh on every keystroke, so editor instances can update them after mount.
let _twigCompletions = []
let _twigMacros = []
let _twigProviderDisposable = null
let _twigHoverDisposable = null

// ---------------------------------------------------------------------------
// Twig syntax knowledge — used by both the suggest + hover providers below.
// ---------------------------------------------------------------------------

// Statement-level tags that open / close a Twig block. Used inside `{% %}`
// blocks to suggest the right keyword.
const TWIG_TAGS = [
    { keyword: 'if',        snippet: 'if ${1:condition} %}\n\t$0\n{% endif ', desc: 'Conditional block' },
    { keyword: 'elseif',    snippet: 'elseif ${1:condition} ', desc: 'Else-if branch' },
    { keyword: 'else',      snippet: 'else ', desc: 'Else branch' },
    { keyword: 'endif',     snippet: 'endif ', desc: 'Close conditional block' },
    { keyword: 'for',       snippet: 'for ${1:item} in ${2:items} %}\n\t$0\n{% endfor ', desc: 'Loop block' },
    { keyword: 'endfor',    snippet: 'endfor ', desc: 'Close loop block' },
    { keyword: 'set',       snippet: 'set ${1:variable} = ${2:value} ', desc: 'Assign variable' },
    { keyword: 'block',     snippet: 'block ${1:name} %}\n\t$0\n{% endblock ', desc: 'Inheritance block' },
    { keyword: 'endblock',  snippet: 'endblock ', desc: 'Close inheritance block' },
    { keyword: 'macro',     snippet: 'macro ${1:name}(${2:args}) %}\n\t$0\n{% endmacro ', desc: 'Define macro' },
    { keyword: 'endmacro',  snippet: 'endmacro ', desc: 'Close macro definition' },
    { keyword: 'include',   snippet: 'include "${1:partial.twig}" ', desc: 'Include another template' },
    { keyword: 'extends',   snippet: 'extends "${1:layout.twig}" ', desc: 'Extend a base template' },
    { keyword: 'import',    snippet: 'import "${1:macros.twig}" as ${2:m} ', desc: 'Import macros from another template' },
    { keyword: 'from',      snippet: 'from _self import ${1:macro_name} ', desc: 'Import macro from current template' },
    { keyword: 'apply',     snippet: 'apply ${1:filter} %}\n\t$0\n{% endapply ', desc: 'Apply a filter to a block' },
    { keyword: 'endapply',  snippet: 'endapply ', desc: 'Close apply block' },
    { keyword: 'with',      snippet: 'with { ${1:var}: ${2:value} } %}\n\t$0\n{% endwith ', desc: 'Scoped variable block' },
    { keyword: 'endwith',   snippet: 'endwith ', desc: 'Close scoped variable block' },
    { keyword: 'verbatim',  snippet: 'verbatim %}\n\t$0\n{% endverbatim ', desc: 'Output Twig syntax verbatim' },
    { keyword: 'endverbatim', snippet: 'endverbatim ', desc: 'Close verbatim block' },
]

// Twig built-in filters (subset most relevant to email/PDF templates).
// The `args` field is appended as a Monaco snippet so the user can tab
// through filter parameters.
const TWIG_FILTERS = [
    { name: 'abs',        args: '', desc: 'Absolute value' },
    { name: 'capitalize', args: '', desc: 'First character to uppercase' },
    { name: 'date',       args: '("${1:d.m.Y}")', desc: 'Format a date' },
    { name: 'date_modify',args: '("${1:+1 day}")', desc: 'Modify a date' },
    { name: 'default',    args: '("${1:fallback}")', desc: 'Default value if empty' },
    { name: 'escape',     args: '', desc: 'Escape HTML (alias e)' },
    { name: 'e',          args: '', desc: 'Escape HTML (alias of escape)' },
    { name: 'first',      args: '', desc: 'First element of array' },
    { name: 'format',     args: '(${1:args})', desc: 'sprintf-style format' },
    { name: 'join',       args: '("${1:, }")', desc: 'Join array into string' },
    { name: 'json_encode',args: '', desc: 'JSON-encode value' },
    { name: 'keys',       args: '', desc: 'Keys of an array' },
    { name: 'last',       args: '', desc: 'Last element of array' },
    { name: 'length',     args: '', desc: 'Number of elements' },
    { name: 'lower',      args: '', desc: 'Lowercase string' },
    { name: 'merge',      args: '(${1:other})', desc: 'Merge arrays' },
    { name: 'nl2br',      args: '', desc: 'Newlines → <br>' },
    { name: 'number_format', args: '(${1:2}, "${2:,}", "${3:.}")', desc: 'Format a number' },
    { name: 'raw',        args: '', desc: 'Mark as safe (no escaping)' },
    { name: 'replace',    args: '({"${1:from}": "${2:to}"})', desc: 'Replace substrings' },
    { name: 'reverse',    args: '', desc: 'Reverse array/string' },
    { name: 'round',      args: '(${1:0})', desc: 'Round number' },
    { name: 'slice',      args: '(${1:start}, ${2:length})', desc: 'Slice array/string' },
    { name: 'sort',       args: '', desc: 'Sort array' },
    { name: 'split',      args: '("${1:,}")', desc: 'Split string' },
    { name: 'striptags',  args: '', desc: 'Strip HTML tags' },
    { name: 'title',      args: '', desc: 'Title-case string' },
    { name: 'trim',       args: '', desc: 'Trim whitespace' },
    { name: 'upper',      args: '', desc: 'Uppercase string' },
    { name: 'url_encode', args: '', desc: 'URL-encode value' },
    // Project-specific (registered in PurchaseConfirmation.php twig env)
    { name: 'ucwords',    args: '', desc: 'Project filter: uppercase words' },
]

/**
 * Build a Monaco suggestion list of `_twigCompletions` filtered by the
 * dotted prefix the user has typed. Handles two narrowing modes:
 *   - `order.` (trailing dot)  → keys directly under order.* one level deep
 *   - `order` / `or`          → top-level identifiers starting with the
 *                                typed text (depth-1 completions)
 *
 * Each completion item also reports its label + group as the description
 * row so admins see the human-readable name next to the raw key.
 */
function buildVariableSuggestions(monaco, typed, position) {
    const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column - typed.length,
        endColumn: position.column,
    }

    let items = _twigCompletions
    if (typed.includes('.')) {
        // Dot path: narrow to siblings of the current path.
        // typed = "order.summary." → match all that start with "order.summary."
        // typed = "order.summa"    → match all that start with "order.summa"
        const lastDot = typed.lastIndexOf('.')
        const prefix = typed.slice(0, lastDot + 1) // includes the trailing dot
        items = items.filter(c => c.value.startsWith(prefix))
    }

    return {
        suggestions: items.map(item => ({
            label: { label: item.value, description: item.label },
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: item.value,
            detail: item.group || '',
            documentation: item.label,
            range,
            // Sort by group then alpha so logically-related vars cluster.
            sortText: (item.group || 'zz') + ':' + item.value,
        })),
    }
}

/**
 * Build a Monaco suggestion list of `_twigMacros` (passed in by the parent
 * via the `macros` prop — generic editor remains domain-agnostic).
 *
 * Each macro item has shape `{ name, signature?, snippet?, desc? }`:
 *   - `name`      callable identifier (required, e.g. `articles`)
 *   - `signature` human-readable hint, e.g. `articles(order)` (optional)
 *   - `snippet`   Monaco snippet inserted on accept; falls back to
 *                 `name(${1})` if omitted (optional)
 *   - `desc`      one-line description (optional)
 */
function buildMacroSuggestions(monaco, typed, position) {
    if (!_twigMacros.length) return []
    const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column - typed.length,
        endColumn: position.column,
    }
    return _twigMacros.map(macro => ({
        label: { label: macro.name, description: macro.signature || macro.desc || '' },
        kind: monaco.languages.CompletionItemKind.Function,
        insertText: macro.snippet || (macro.name + '(${1})'),
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        detail: 'Twig macro',
        documentation: macro.desc || macro.signature || '',
        range,
        sortText: 'macro:' + macro.name,
    }))
}

// Top-level snippet starters offered when no Twig context is detected.
const TWIG_SNIPPETS = [
    {
        label: 'if',
        documentation: 'Conditional block',
        insertText: '{% if ${1:condition} %}\n\t$0\n{% endif %}',
    },
    {
        label: 'ifelse',
        documentation: 'Conditional with else branch',
        insertText: '{% if ${1:condition} %}\n\t$2\n{% else %}\n\t$0\n{% endif %}',
    },
    {
        label: 'for',
        documentation: 'Loop over an array',
        insertText: '{% for ${1:item} in ${2:items} %}\n\t$0\n{% endfor %}',
    },
    {
        label: 'set',
        documentation: 'Assign a variable',
        insertText: '{% set ${1:variable} = ${2:value} %}',
    },
    {
        label: 'include',
        documentation: 'Include another template',
        insertText: '{% include "${1:partial.twig}" %}',
    },
    {
        label: 'block',
        documentation: 'Inheritance block',
        insertText: '{% block ${1:name} %}\n\t$0\n{% endblock %}',
    },
    {
        label: 'macro',
        documentation: 'Define a macro',
        insertText: '{% macro ${1:name}(${2:args}) %}\n\t$0\n{% endmacro %}',
    },
    {
        label: 'comment',
        documentation: 'Twig comment block',
        insertText: '{# $0 #}',
    },
    {
        label: 'interpolate',
        documentation: 'Output an expression',
        insertText: '{{ $0 }}',
    },
]

export default {
    name: 'MonacoEditor',
    props: {
        value: {
            default: undefined
        },
        language: {
            type: String,
            required: true,
            validator(value) {
                // The value must match one of these strings
                return ['html', 'twig', 'js', 'json', 'javascript', 'css'].includes(value)
            }
        },
        disabled: {
            type: Boolean,
            default: false
        },
        completions: {
            type: Array,
            default: () => []
        },
        // Optional list of callable Twig macros for the current scope —
        // shape: { name, signature?, snippet?, desc? }. The MonacoEditor
        // component knows nothing about which macros exist where; the parent
        // (e.g. AbonementDocumentTemplates) computes this list from the
        // active template's scope and passes it down.
        macros: {
            type: Array,
            default: () => []
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        minHeight: {
            type: Number,
            default: 138
        }
    },
    data () {
        return {
            content: this.value,
            editor: null,
            monaco: null,
            height: this.minHeight,
            focused: false,
        }
    },
    components: {
        VueMonacoEditor,
    },
    watch: {
        value(value) {
            if (value !== this.content) {
                this.content = value;
            }

            // if (!this.disabled) {
            //     this.$emit('input', value);
            // }
        },
        completions(items) {
            if (this.language === 'twig') _twigCompletions = items
        },
        macros(items) {
            if (this.language === 'twig') _twigMacros = items || []
        },
        // disabled(disabled) {
        //     if (disabled) {
        //         this.editor.updateOptions({
        //             readOnly: true,
        //             selectionHighlight: false,
        //             occurrencesHighlight: false,
        //             renderLineHighlight: 'none',
        //         });
        //     } else {
        //         this.editor.updateOptions({
        //             readOnly: false,
        //             selectionHighlight: true,
        //             occurrencesHighlight: true,
        //             renderLineHighlight: 'all',
        //         });
        //     }
        // }
    },
    computed: {
        options() {
            return {
                // height: this.height,
                fontSize: 14,
                // fontFamily: 'Jetbrains-Mono',
                fontFamily: 'Fira Code',
                fontLigatures: true,
                automaticLayout: true,
                formatOnType: true,
                formatOnPaste: true,
                scrollBeyondLastLine: false,
                readOnly: this.readOnly || this.disabled,
                domReadOnly: this.readOnly || this.disabled,
                renderIndentGuides: !this.disabled,
                selectionHighlight: !this.disabled,
                occurrencesHighlight: !this.disabled,
                renderLineHighlight: (this.readOnly || this.disabled) ? 'none' : 'all',
                // colorDecorators: !this.disabled,
                colorDecorators: false,
                colorDecoratorsActivatedOn: 'click',
                renderLineHighlightOnlyWhenFocus: true,
                wordWrap: 'on',
                wrappingStrategy: 'advanced',
                // Render overflow widgets (autocomplete suggest box, hover
                // tooltips, parameter hints, definition previews) in a
                // position: fixed layer attached to <body> instead of inside
                // the editor's overflow:auto container. Without this, any
                // tooltip taller/wider than the editor gets clipped because
                // .monaco-editor-container itself scrolls.
                fixedOverflowWidgets: true,
                minimap: {
                    enabled: !this.disabled,
                },
                padding: {
                    top: 8,
                    bottom: 8,
                },
                overviewRulerLanes: 0.
            }
        }
    },
    methods: {
        handleBeforeMount(monaco) {
        },
        handleMount(editor, monaco) {
            this.editor = editor;
            this.monaco = monaco;

            this.updateHeight();

            // this.monaco.editor.defineTheme('github-dark', githubDarkTheme)
            // this.monaco.editor.setTheme('github-dark')

            this.monaco.editor.defineTheme('one-dark', oneDarkTheme)
            this.monaco.editor.setTheme('one-dark')

            if (this.language === 'twig') {
                if (this.completions && this.completions.length) _twigCompletions = this.completions
                if (this.macros && this.macros.length) _twigMacros = this.macros
                if (!_twigProviderDisposable) {
                    _twigProviderDisposable = monaco.languages.registerCompletionItemProvider('twig', {
                        // Trigger on '{', whitespace, '.', '|' so users get
                        // suggestions both when opening blocks and when
                        // narrowing dot-paths or starting a filter.
                        triggerCharacters: ['{', ' ', '.', '|'],
                        provideCompletionItems(model, position) {
                            const line = model.getLineContent(position.lineNumber)
                            const before = line.substring(0, position.column - 1)

                            // 1. Filter context — `... | filter` after a pipe
                            // inside an interpolation or statement block.
                            //    `{{ foo | u` or `{% set x = bar | u`
                            const filterMatch = before.match(/(\{\{|\{%)[^|]*\|\s*([\w]*)$/)
                            if (filterMatch) {
                                const typed = filterMatch[2]
                                const range = {
                                    startLineNumber: position.lineNumber,
                                    endLineNumber: position.lineNumber,
                                    startColumn: position.column - typed.length,
                                    endColumn: position.column,
                                }
                                return {
                                    suggestions: TWIG_FILTERS.map(f => ({
                                        label: { label: f.name, description: f.desc },
                                        kind: monaco.languages.CompletionItemKind.Function,
                                        insertText: f.name + (f.args || ''),
                                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                        detail: 'Twig filter',
                                        documentation: f.desc,
                                        range,
                                    })),
                                }
                            }

                            // 2. Interpolation context — `{{ … }}`. Offer
                            // variables AND callable macros (and respect
                            // dot-path narrowing for variables).
                            //    `{{ ord` → top-level vars matching ord*
                            //    `{{ order.` → keys under order.*
                            //    `{{ items` → matches `items(return)` macro
                            //                  + `order.items` / `return.items`
                            const interpMatch = before.match(/\{\{-?\s*([\w.]*)$/)
                            if (interpMatch) {
                                const sug = buildVariableSuggestions(monaco, interpMatch[1], position)
                                sug.suggestions.push(...buildMacroSuggestions(monaco, interpMatch[1], position))
                                return sug
                            }

                            // 3. Statement context — `{% … %}`. Offer Twig
                            // tags (after `{%`) AND variables (after a tag
                            // keyword like `if`, `elseif`, `for … in `).
                            //    `{% if `, `{% set foo = `, `{% for x in `
                            const stmtTagMatch = before.match(/\{%-?\s*([\w]*)$/)
                            if (stmtTagMatch) {
                                const typed = stmtTagMatch[1]
                                const range = {
                                    startLineNumber: position.lineNumber,
                                    endLineNumber: position.lineNumber,
                                    startColumn: position.column - typed.length,
                                    endColumn: position.column,
                                }
                                return {
                                    suggestions: TWIG_TAGS.map(t => ({
                                        label: { label: t.keyword, description: t.desc },
                                        kind: monaco.languages.CompletionItemKind.Keyword,
                                        insertText: t.snippet,
                                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                        detail: 'Twig tag',
                                        documentation: t.desc,
                                        range,
                                    })),
                                }
                            }

                            // Inside a `{% … %}` block past the leading tag:
                            // suggest variables AND macros (the user might be
                            // typing an expression like
                            // `{% set html = items(return) %}` or
                            // `{% if order.is_revocable %}`).
                            const stmtVarMatch = before.match(/\{%-?\s*\w+[^%]*?(?:[\s(=,])([\w.]*)$/)
                            if (stmtVarMatch) {
                                const sug = buildVariableSuggestions(monaco, stmtVarMatch[1], position)
                                sug.suggestions.push(...buildMacroSuggestions(monaco, stmtVarMatch[1], position))
                                return sug
                            }

                            // 4. Outside any twig context — offer top-level
                            // snippets (`if`, `for`, `set`, `interpolate`)
                            // so users can scaffold a fresh block fast.
                            const lastChar = before.slice(-1)
                            const wordMatch = before.match(/(\w+)$/)
                            if (wordMatch && !lastChar.match(/[{%]/)) {
                                const typed = wordMatch[1]
                                const range = {
                                    startLineNumber: position.lineNumber,
                                    endLineNumber: position.lineNumber,
                                    startColumn: position.column - typed.length,
                                    endColumn: position.column,
                                }
                                return {
                                    suggestions: TWIG_SNIPPETS.map(s => ({
                                        label: { label: s.label, description: s.documentation },
                                        kind: monaco.languages.CompletionItemKind.Snippet,
                                        insertText: s.insertText,
                                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                        detail: 'Twig snippet',
                                        documentation: s.documentation,
                                        range,
                                    })),
                                }
                            }

                            return { suggestions: [] }
                        },
                    })
                }

                // Hover provider — show the human-readable label/group of a
                // variable when the user mouses over it. Resolves the longest
                // matching prefix so e.g. `order.invoice_nr` matches whether
                // the user hovers `order` or the full path.
                if (!_twigHoverDisposable) {
                    _twigHoverDisposable = monaco.languages.registerHoverProvider('twig', {
                        provideHover(model, position) {
                            const line = model.getLineContent(position.lineNumber)
                            // Capture the dotted identifier the cursor is on.
                            const left = line.substring(0, position.column - 1).match(/[\w.]*$/)?.[0] || ''
                            const right = line.substring(position.column - 1).match(/^[\w.]*/)?.[0] || ''
                            const identifier = left + right
                            if (!identifier) return null
                            // Longest matching variable wins.
                            const match = _twigCompletions
                                .filter(c => c.value === identifier || identifier.startsWith(c.value + '.'))
                                .sort((a, b) => b.value.length - a.value.length)[0]
                            if (!match) return null
                            const startCol = position.column - left.length
                            const endCol = position.column + right.length
                            return {
                                range: new monaco.Range(position.lineNumber, startCol, position.lineNumber, endCol),
                                contents: [
                                    { value: '**' + match.value + '**' },
                                    { value: match.label },
                                    ...(match.group ? [{ value: '_' + match.group + '_' }] : []),
                                ],
                            }
                        },
                    })
                }
            }

            this.editor.onDidChangeModelContent(() => {
                this.$emit('input', this.editor.getValue());
            });

            this.editor.onDidContentSizeChange(this.updateHeight);

            this.editor.onDidFocusEditorWidget(() => {
                this.focused = true;
            });

            this.editor.onDidBlurEditorWidget(() => {
                this.focused = false;
            });

            // const observer = new ResizeObserver(this.handleResize);
            // observer.observe(this.$refs.container);
        },
        updateHeight() {
            const contentHeight = this.editor.getContentHeight();
            this.height = contentHeight;
            // this.editor.layout({ height: contentHeight });
        },
        handleResize() {
            // this.editor.layout()
        },
    },
}
</script>

<style lang="sass" scoped>
.monaco-editor-container
    position: relative
    resize: vertical
    overflow: auto
    border-radius: 6px
    isolation: isolate
    min-height: var(--min-height, 138px)
    height: 26rem
    max-height: var(--height)
    &.focused::v-deep
        z-index: 5
    // &.focused
    //     overflow: visible
    &.disabled
        filter: contrast(0.8) brightness(1.2) saturate(40%)
        // opacity: 0.8
    .editor
        position: absolute
        inset: 0
</style>
